import React, { useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import ResourceAssignmentField from '../ResourceAssignmentField';
import {
  useResourceRelationships,
  useUpdateResourceRelationships,
} from 'hooks/resources/useResourceRelationships';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import {
  STATUS_LABELS,
  CHECKED_IN_STATUSES,
} from 'components/IAP/Resources/constants/resourceConstants';
import { MemberResourceAssignmentProps } from './types';

const MemberResourceAssignment: React.FC<MemberResourceAssignmentProps> = ({
  incidentMemberId,
  isUserCheckedIn,
  hasCheckedInResources: _parentHasCheckedInResources,
  userGuid: _userGuid,
}) => {
  const form = useForm<{ resources: string[] }>({
    defaultValues: {
      resources: [],
    },
  });

  const { data: memberResources = [] } = useResourceRelationships(
    incidentMemberId,
    'member'
  );
  const updateRelationships = useUpdateResourceRelationships();

  const { data: resourcesInIncident = [] } = useResourcesInIncident();
  const { data: allResourceSets = [] } = useResourceSets();

  const availableResources = useMemo(() => {
    return allResourceSets.map((resourceSet) => {
      const resourceSetId = resourceSet.resource_set_id;
      const incidentResource = resourcesInIncident.find(
        (r) => r.resource_set_id === resourceSetId
      );

      return {
        ...resourceSet,
        id: resourceSet.resource_set_id,
        /* eslint-disable camelcase */
        resource_set_id: resourceSet.resource_set_id,
        current_status: incidentResource?.current_status,
        /* eslint-enable camelcase */
      };
    });
  }, [allResourceSets, resourcesInIncident]);

  const hasCheckedInResources = useMemo(() => {
    if (!memberResources || memberResources.length === 0) return false;

    const memberResourceSetIds = memberResources.map((r) => r.resource_set_id);
    return resourcesInIncident.some(
      (resource) =>
        memberResourceSetIds.includes(resource.resource_set_id) &&
        CHECKED_IN_STATUSES.some((status) => status === resource.current_status)
    );
  }, [memberResources, resourcesInIncident]);

  useEffect(() => {
    const resourceIds = memberResources.map((r) => r.resource_set_id);
    form.setValue('resources', resourceIds);
  }, [memberResources, form]);

  const [isEditingResources, setIsEditingResources] = useState(false);
  const [isModifyingResources, setIsModifyingResources] = useState(false);
  const [originalResourceIds, setOriginalResourceIds] = useState<string[]>([]);

  const handleEditResources = () => {
    setOriginalResourceIds(memberResources.map((r) => r.resource_set_id));
    setIsEditingResources(true);
  };

  const handleSaveResources = async () => {
    const currentIds = form.getValues('resources') || [];

    if (isUserCheckedIn && hasCheckedInResources) {
      const originalResourceSetIds = memberResources.map(
        (r) => r.resource_set_id
      );
      const removedResourceIds = originalResourceSetIds.filter(
        (id) => !currentIds.includes(id)
      );

      if (removedResourceIds.length > 0) {
        const hasRemovedCheckedInResource = resourcesInIncident.some(
          (resource) =>
            removedResourceIds.includes(resource.resource_set_id) &&
            CHECKED_IN_STATUSES.some(
              (status) => status === resource.current_status
            )
        );

        if (hasRemovedCheckedInResource) {
          toast.error(
            'Cannot remove checked-in resources while user is checked in. Please check out resources first.',
            { autoClose: 3000 }
          );
          return;
        }
      }
    }

    setIsModifyingResources(true);
    try {
      await updateRelationships.mutateAsync({
        entityId: incidentMemberId,
        entityType: 'member',
        relatedIds: currentIds,
      });
      setIsEditingResources(false);
    } catch (error) {
      console.error('Failed to save resource changes:', error);
      form.setValue('resources', originalResourceIds);
    } finally {
      setIsModifyingResources(false);
    }
  };

  const handleCancelResources = () => {
    form.setValue('resources', originalResourceIds);
    setIsEditingResources(false);
  };

  const formatResourceDisplay = (resource: {
    id: string;
    name?: string;
    kind?: string;
  }) => {
    const resourceWithStatus = availableResources.find(
      (r) => r.id === resource.id
    );

    const parts = [resource.name];

    const kindToUse = resourceWithStatus?.kind || resource.kind;
    if (kindToUse) {
      parts.push(kindToUse);
    }

    let statusPart = null;
    if (resourceWithStatus?.current_status) {
      const statusLabel =
        STATUS_LABELS[resourceWithStatus.current_status] ||
        resourceWithStatus.current_status;
      statusPart = statusLabel;
    }

    return (
      <span>
        {parts[0]}
        {parts[1] && <> - {parts[1]}</>}
        {statusPart && (
          <>
            {' '}
            - <i>({statusPart})</i>
          </>
        )}
      </span>
    );
  };

  return (
    <FormProvider {...form}>
      <div className="row mt-3">
        <div className="col-12">
          {hasCheckedInResources && (
            <div className="alert alert-warning mb-2">
              <small>
                Resource assignments cannot be modified while resources are
                checked in. Check out all resources before modifying
                assignments.
              </small>
            </div>
          )}
          {!isEditingResources ? (
            <div>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="txt--semibold pt-4">
                  Assigned Resources: {memberResources.length}
                </div>
                <StylishNewButton
                  className="button button--icon button--sml sml border-0"
                  onClick={handleEditResources}
                  disabled={hasCheckedInResources}
                >
                  <SharedIcon iconName="edit" size={'sm'} />
                </StylishNewButton>
              </div>
              <div>
                {memberResources.map((resource) => (
                  <span
                    key={resource.id}
                    className="badge badge--sm badge--neutral me-2"
                  >
                    {formatResourceDisplay(resource)}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <>
              <ResourceAssignmentField
                memberResources={memberResources}
                availableResources={availableResources || []}
                hasCheckedInResources={hasCheckedInResources}
              />
              <div className="d-flex gap-2 mt-3">
                <StylishNewButton
                  className="button--primary"
                  onClick={handleSaveResources}
                  disabled={isModifyingResources}
                >
                  Save
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary"
                  onClick={handleCancelResources}
                  disabled={isModifyingResources}
                >
                  Cancel
                </StylishNewButton>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Status display for resource associations */}
      {isModifyingResources && (
        <div className="d-flex align-items-center mt-3">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">
              Updating resource associations...
            </span>
          </div>
          <span className="text-muted">Updating resource associations...</span>
        </div>
      )}
    </FormProvider>
  );
};

export default MemberResourceAssignment;
